import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { SetStatusModal, SetStatusModalProps } from './SetStatusModal';

/** Props */
interface SetCvStatusModalProps extends Omit<SetStatusModalProps, 'isOpen' | 'onClose'> {
  onCloseCallback?: (isSuccess: boolean) => void,
}

/**
 * @description SetCvStatusModal - A modal component for setting the status of a CV.
 * It wraps the SetStatusModal component.
 * - `locale`: The locale to use for SWR hooks.
 * - `operationJob`: The last job.
 * - `userSlug`: The user slug.
 * - `onCloseCallback`: Optional callback to execute when the modal is closed.
 * - `interviewFolders`: Optional. An array that contains the jobs where the talent already has a status.
 * - `status`: The status to be set. Default to 'shortlist'.
 * - `successToastText`: The text to be displayed in the success toast.
 * - `title`: The text to be displayed as the modal title.
 * - `trackingId`: Optional. The tracking id for the event.
 * - `conditionallyDisableSubmit`: Whether to conditionally disable the 'submit' button based on certain conditions. Default to true.
 * - `keepExistingStatus`: If true, it will ignore the new 'status' and submit the existing one if available. If no existing status, it will use the 'status' prop. Default to false.
 */
export const SetCvStatusModal = NiceModal.create((props: SetCvStatusModalProps) => {
  // Destructure props
  const {
    locale,
    operationJob,
    userSlug,
    onCloseCallback,
    interviewFolders,
    status,
    successToastText,
    title,
    trackingId,
    conditionallyDisableSubmit,
    keepExistingStatus,
  } = props;

  // Use a hook to manage the modal state
  const modal = useModal();

  // Close modal
  const closeModal = () => {
    void modal.hide();
  };

  // Render component
  // *****************************************
  return (
    <SetStatusModal
      isOpen={modal.visible}
      locale={locale}
      operationJob={operationJob}
      userSlug={userSlug}
      interviewFolders={interviewFolders}
      status={status}
      title={title}
      successToastText={successToastText}
      trackingId={trackingId}
      conditionallyDisableSubmit={conditionallyDisableSubmit}
      keepExistingStatus={keepExistingStatus}
      onClose={(isSuccess: boolean) => {
        closeModal();
        if (onCloseCallback) onCloseCallback(isSuccess);
      }}
    />
  );
});

/**
 * @description showSetCvStatusModal - Function to open the SetCvStatusModal (SetStatusModal component).
 * - `locale`: The locale to use for SWR hooks.
 * - `operationJob`: The last job.
 * - `userSlug`: The user slug.
 * - `onCloseCallback`: Optional callback to execute when the modal is closed.
 * - `interviewFolders`: Optional. An array that contains the jobs where the talent already has a status.
 * - `status`: The status to be set. Default to 'shortlist'.
 * - `successToastText`: The text to be displayed in the success toast.
 * - `title`: The text to be displayed as the modal title.
 * - `trackingId`: Optional. The tracking id for the event.
 * - `conditionallyDisableSubmit`: Whether to conditionally disable the 'submit' button based on certain conditions. Default to true.
 * - `keepExistingStatus`: If true, it will ignore the new 'status' and submit the existing one if available. If no existing status, it will use the 'status' prop. Default to false.
 */
export const showSetCvStatusModal = (props: SetCvStatusModalProps) => {
  void NiceModal.show(SetCvStatusModal, props);
};
